import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useActiveProvablyFair, useRoundInfo } from '@hooks/queries'
import { verifyRound } from '@core/provablyFair/crashFairness'
import { getClientSeed, randomiseClientSeed, setClientSeed } from '@core/provablyFair/clientSeed'
import SpinningLoader from '@components/loaders/SpinningLoader'
import HashValue from './HashValue'
import VerificationSteps from './VerificationSteps'
import AiVerificationBlock from './AiVerificationBlock'

// Provably Fair screen.
//
// The important property of this screen: the tick marks come from arithmetic
// this browser performed, not from a success flag the API returned. The round
// payload is fetched, then verifyRound() independently recomputes the
// commitment, the round seed and the crash point with Web Crypto and compares
// them to the recorded values. A backend claiming a wrong crash point would
// show ✕ here.
//
// Which round is shown is decided by the caller, not by a picker on this screen:
// the Verify button on a history chip passes its roundId, and otherwise the most
// recent finished round is used. The crash-history row is already the place where
// players choose a round, so a second chooser here was redundant.
//
// A v2 round also lists the three client seeds that fed it, each labelled with
// where it came from. Distinguishing them is not decoration: a slot nobody claimed
// is filled by the server, and presenting that as a player's contribution would
// overstate what the scheme guarantees for that round.

// Where one client seed came from, in the player's own terms.
//
// A server fallback is called one, plainly. It is a real random seed that filled a
// slot nobody claimed, and the round is exactly as verifiable for it — but calling
// it player-provided would claim entropy from a player who never sent any.
const seedOrigin = (entry, playerId) => {
  if (entry.isFallback) return 'server fallback'
  if (playerId && entry.playerId === playerId) return 'yours'
  return 'another player'
}

const ProvablyFair = ({ initialRoundId = null }) => {
  const gameCode = useSelector((state) => state.session.details.gameCode)
  const gameMode = useSelector((state) => state.session.details.gameMode)
  const playerId = useSelector((state) => state.session.details.playerId)
  const lastRounds = useSelector((state) => state.events.lastCrashMultipliers ?? [])
  const liveRoundId = useSelector((state) => state.events.round?.roundId ?? null)
  // The live round's own commitment, published on its round-state events.
  const liveServerSeedHash = useSelector((state) => state.events.round?.serverSeedHash ?? null)

  // Keyed by roundId so a result can never be shown against a different round.
  const [verification, setVerification] = useState(null)

  // What the player is typing, and the last outcome of saving.
  const [seedDraft, setSeedDraft] = useState('')
  const [seedStatus, setSeedStatus] = useState(null)

  // Read during render, deliberately: no state to mirror, no effect to sync, and
  // nothing to invalidate. getClientSeed is a Map lookup after the first call, and
  // that first call mints the seed once and persists it — so repeated renders are
  // pure reads returning a stable value.
  //
  // Saving already re-renders (setSeedStatus), so the row picks up a new seed
  // without a version counter; and reading here rather than in an effect means a
  // session arriving after mount is covered too.
  const activeSeed = getClientSeed(playerId)

  const applySeedResult = useCallback((result) => {
    if (!result.ok) {
      setSeedStatus({ type: 'error', text: result.error })
      return
    }

    setSeedDraft('')
    setSeedStatus({
      type: 'saved',
      // Said plainly when it happens: a seed that silently vanishes on reload,
      // in an operator iframe that blocks storage, would look like a bug.
      text: result.persisted
        ? 'Saved. It applies from your next bet.'
        : 'Saved for this session — this browser is blocking storage, so it will reset on reload.',
    })
  }, [])

  const handleSaveSeed = useCallback(
    () => applySeedResult(setClientSeed(seedDraft, playerId)),
    [applySeedResult, seedDraft, playerId]
  )

  const handleRandomiseSeed = useCallback(
    () => applySeedResult(randomiseClientSeed(playerId)),
    [applySeedResult, playerId]
  )

  // Newest finished round, used when the screen was opened without a specific one.
  // The live round is excluded — its seed does not exist yet, by design.
  const newestFinishedRoundId = useMemo(
    () =>
      lastRounds.find((entry) => entry?.roundId && entry.roundId !== liveRoundId)?.roundId ?? null,
    [lastRounds, liveRoundId]
  )

  const effectiveRoundId = initialRoundId ?? newestFinishedRoundId

  const { data: commitment, isLoading: isCommitmentLoading } = useActiveProvablyFair({
    gameCode,
    gameMode,
  })

  const {
    data: round,
    isLoading: isRoundLoading,
    isError: isRoundError,
    error: roundError,
  } = useRoundInfo({ roundId: effectiveRoundId })

  // Recompute locally whenever a new round payload arrives. Web Crypto is async,
  // so the state write happens in the promise callback.
  useEffect(() => {
    const payload = round?.raw
    if (!payload) return undefined

    let cancelled = false

    verifyRound(payload).then((result) => {
      if (!cancelled) setVerification({ roundId: payload.roundId, result })
    })

    return () => {
      cancelled = true
    }
  }, [round])

  // Only trust a stored result if it belongs to the round on screen.
  const activeVerification =
    round && verification?.roundId === round.roundId ? verification.result : null
  const isVerifying = Boolean(round) && !activeVerification

  return (
    <div className="standard-normal-provably-fair-container">
      {/* ── The player's own client seed ──────────────────────────────────
          First, because it is the only thing on this screen a player can
          actually change. Everything below is a record to read. */}
      <section className="standard-normal-provably-fair-section">
        <h3 className="standard-normal-provably-fair-heading">Your Client Seed</h3>

        <HashValue
          label="In use for your next bet"
          value={activeSeed}
          placeholder={playerId ? 'Unavailable in this browser' : 'Sign in to get a seed'}
        />

        <div className="standard-normal-provably-fair-action-row">
          <input
            className={`standard-normal-provably-fair-input ${seedStatus?.type === 'error' ? 'is-invalid' : ''}`}
            value={seedDraft}
            onChange={(event) => {
              setSeedDraft(event.target.value)
              setSeedStatus(null)
            }}
            placeholder="New client seed"
            aria-label="New client seed"
            maxLength={64}
            spellCheck={false}
            autoComplete="off"
            disabled={!playerId}
          />
          <button
            type="button"
            className="standard-normal-provably-fair-button"
            onClick={handleSaveSeed}
            disabled={!playerId || !seedDraft.trim()}
          >
            Save
          </button>
          <button
            type="button"
            className="standard-normal-provably-fair-button standard-normal-provably-fair-button--ghost"
            onClick={handleRandomiseSeed}
            disabled={!playerId}
            title="Generate a random seed"
          >
            Random
          </button>
        </div>

        {seedStatus && (
          <span
            className={`standard-normal-provably-fair-seed-status is-${seedStatus.type}`}
            role={seedStatus.type === 'error' ? 'alert' : undefined}
          >
            {seedStatus.text}
          </span>
        )}

        <p className="standard-normal-provably-fair-note">
          This seed is sent with every bet you place and is mixed into the round result
          alongside the server seed. A new seed applies from your next round — the one in
          play is already sealed, and being able to change that after the fact is exactly
          what the scheme prevents.
        </p>
      </section>

      {/* ── This round's commitment ──────────────────────────────────────
          There is no system-wide "current seed" to show: every round has its own,
          so the live commitment is the one the server published for the round in
          play. It arrives on the round-state events, not from a query. */}
      <section className="standard-normal-provably-fair-section">
        <h3 className="standard-normal-provably-fair-heading">Current Round Commitment</h3>

        {isCommitmentLoading && !liveServerSeedHash && <SpinningLoader />}

        <HashValue
          label="Server Seed Hash (round in play)"
          value={liveServerSeedHash}
          placeholder="Waiting for the next round"
        />

        <div className="standard-normal-provably-fair-meta-grid">
          <div className="standard-normal-provably-fair-meta is-wide">
            <span className="standard-normal-provably-fair-meta-label">Round ID</span>
            <span className="standard-normal-provably-fair-meta-value">{liveRoundId ?? '—'}</span>
          </div>
          <div className="standard-normal-provably-fair-meta">
            <span className="standard-normal-provably-fair-meta-label">House Edge</span>
            <span className="standard-normal-provably-fair-meta-value">
              {commitment?.houseEdgePercent ?? '—'}
            </span>
          </div>
          <div className="standard-normal-provably-fair-meta">
            <span className="standard-normal-provably-fair-meta-label">Seed Model</span>
            <span className="standard-normal-provably-fair-meta-value">One per round</span>
          </div>
        </div>
      </section>

      {/* ── The selected round ──────────────────────────────────────────── */}
      {effectiveRoundId && (
        <section className="standard-normal-provably-fair-section">
          {(isRoundLoading || isVerifying) && <SpinningLoader />}

          {isRoundError && (
            <p className="standard-normal-provably-fair-error">
              {roundError?.message ?? 'Could not load that round.'}
            </p>
          )}

          {!isRoundLoading && !isRoundError && !round && (
            <p className="standard-normal-provably-fair-error">
              No verification data for this round yet. Seeds are published once a round has
              finished.
            </p>
          )}

          {round && (
            <>
              <div className="standard-normal-provably-fair-meta-grid">
                <div className="standard-normal-provably-fair-meta">
                  <span className="standard-normal-provably-fair-meta-label">Round ID</span>
                  <span className="standard-normal-provably-fair-meta-value">{round.roundId}</span>
                </div>
                <div className="standard-normal-provably-fair-meta">
                  <span className="standard-normal-provably-fair-meta-label">Crash Point</span>
                  <span className="standard-normal-provably-fair-meta-value is-emphasis">
                    {round.crashPoint}
                  </span>
                </div>
                {/* Only legacy rounds have a position within a shared seed. Hidden
                    rather than shown as "—" for per-round seeds, where it has no
                    meaning. */}
                {round.seedRound && (
                  <div className="standard-normal-provably-fair-meta">
                    <span className="standard-normal-provably-fair-meta-label">Seed Round</span>
                    <span className="standard-normal-provably-fair-meta-value">
                      {round.seedRound}
                    </span>
                  </div>
                )}
                <div className="standard-normal-provably-fair-meta">
                  <span className="standard-normal-provably-fair-meta-label">Played</span>
                  <span className="standard-normal-provably-fair-meta-value">{round.date}</span>
                </div>
              </div>

              <HashValue label="Round Seed" value={round.hash} />
              <HashValue label="Game Seed" value={round.seed} />
              <HashValue label="Server Seed Hash (this round)" value={round.serverSeedHash} />
              <HashValue
                label="Server Seed"
                value={round.serverSeed}
                placeholder="Published once the round ends"
              />

              {/* The three client seeds, in the order the server accepted them —
                  which is the order they were hashed in. Only rendered for a v2
                  round: a v1 round had no client seeds, and three empty rows would
                  imply it was missing something it never had. */}
              {round.clientSeeds?.map((entry) => (
                <HashValue
                  key={entry.slot}
                  label={`Client Seed ${entry.slot} — ${seedOrigin(entry, playerId)}`}
                  value={entry.clientSeed}
                />
              ))}

              {isVerifying ? null : (
                <VerificationSteps verification={activeVerification} round={round} />
              )}

              <AiVerificationBlock round={round} gameCode={gameCode} />
            </>
          )}
        </section>
      )}

      {/* ── How the result is produced ────────────────────────────────────
          Last: reference material, read once. The values above are what a player
          actually came here for. */}
      <section className="standard-normal-provably-fair-section">
        <h3 className="standard-normal-provably-fair-heading">How the Crash Point Is Made</h3>
        <ol className="standard-normal-provably-fair-steps-list">
          <li>
            <code>serverSeedHash = SHA256(serverSeed)</code> is published before betting opens.
          </li>
          <li>
            The first three players to bet each add a client seed. Any slot nobody takes is
            filled with a random server seed.
          </li>
          <li>
            <code>
              roundSeed = SHA256(serverSeed + &quot;:&quot; + roundId + &quot;:&quot; + seed1 +
              &quot;:&quot; + seed2 + &quot;:&quot; + seed3)
            </code>
          </li>
          <li>
            <code>bytes = HMAC_SHA256(key = roundSeed, msg = gameSeed)</code>
          </li>
          <li>
            <code>raw = first 4 bytes as a big-endian number</code>
          </li>
          <li>
            <code>crashPoint = (maxRawOutcome / (raw + 1)) × (1 − houseEdge)</code>
          </li>
        </ol>
        <p className="standard-normal-provably-fair-note">
          One round has one result, so every player in it gets the same crash point. Your seed is
          one of three inputs and the server cannot see it before committing to its own — so
          neither you nor the operator can choose the outcome alone.
        </p>
      </section>
    </div>
  )
}

export default ProvablyFair
